/* ===================================================================
   <cn-marquee> — standalone port of the Horizon marquee engine.
   No build step / imports. Mirrors Horizon's behaviour:
   - duplicates the item group enough times to fill the viewport
   - clones the whole content once so the loop is seamless
   - animation speed scales with sqrt(copies) * data-speed-factor
   - smoothly slows down on hover and speeds back up on leave
     (Web Animations API updatePlaybackRate, eased)

   Markup:
   <cn-marquee data-speed-factor="25" style="--marquee-gap:4rem">
     <div data-marquee-wrapper>
       <div data-marquee-content>
         <div data-marquee-items> ...items... </div>
       </div>
     </div>
   </cn-marquee>
   =================================================================== */
(function () {
  if (customElements.get('cn-marquee')) return;

  var ANIM_DURATION = 500;

  function debounce(fn, wait) {
    var t;
    function d() {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    }
    d.cancel = function () { clearTimeout(t); };
    return d;
  }

  // Animate a number from -> to over `duration`, smoothstep easing.
  function animateValue(opts) {
    var from = opts.from, to = opts.to, duration = opts.duration;
    var onUpdate = opts.onUpdate, onComplete = opts.onComplete;
    var easing = opts.easing || function (t) { return t * t * (3 - 2 * t); };
    var start = performance.now();
    var cancelled = false;
    var current = from;
    function step(now) {
      if (cancelled) return;
      var p = Math.min((now - start) / duration, 1);
      current = from + (to - from) * easing(p);
      onUpdate(current);
      if (p < 1) requestAnimationFrame(step);
      else if (onComplete) onComplete();
    }
    requestAnimationFrame(step);
    return {
      get current() { return current; },
      cancel: function () { cancelled = true; }
    };
  }

  class CnMarquee extends HTMLElement {
    connectedCallback() {
      this._wrapper = this.querySelector('[data-marquee-wrapper]');
      this._content = this.querySelector('[data-marquee-content]');
      this._firstItems = this._content && this._content.querySelector('[data-marquee-items]');
      if (!this._wrapper || !this._content || !this._firstItems) return;

      this._anim = null;
      this._onResize = debounce(this._build.bind(this, true), 250);
      this._slowDown = debounce(this._decelerate.bind(this), ANIM_DURATION);
      this._onEnter = this._slowDown;
      this._onLeave = this._accelerate.bind(this);

      // Measure only once logos have real dimensions, then build.
      this._whenImagesReady().then(function () {
        if (!this.isConnected) return;
        this._build(false);
        window.addEventListener('resize', this._onResize);
        this.addEventListener('pointerenter', this._onEnter);
        this.addEventListener('pointerleave', this._onLeave);
      }.bind(this));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
    }

    _whenImagesReady() {
      var imgs = [].slice.call(this._firstItems.querySelectorAll('img'));
      return Promise.all(imgs.map(function (img) {
        if (img.complete && img.naturalWidth) return null;
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      }));
    }

    /** Rebuild the track to fill the current width. */
    _build(isResize) {
      // Reset to a single item group + remove the previous content clone.
      var groups = [].slice.call(this._content.querySelectorAll('[data-marquee-items]'));
      groups.forEach(function (el, i) { if (i > 0) el.remove(); });
      if (this._wrapper.lastElementChild && this._wrapper.lastElementChild !== this._content) {
        this._wrapper.lastElementChild.remove();
      }

      var marqueeWidth = this.clientWidth;
      var itemsWidth = this._firstItems.getBoundingClientRect().width;
      var copies = itemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemsWidth);

      // Repeat the item group so the content overflows the viewport.
      for (var i = 0; i < copies - 1; i++) {
        this._content.appendChild(this._firstItems.cloneNode(true));
      }

      // Clone the whole content once for a seamless -50% loop.
      var clone = this._content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this._wrapper.appendChild(clone);

      // Speed scales with how much content there is (steady visual pace).
      var factor = Number(this.getAttribute('data-speed-factor')) || 25;
      this.style.setProperty('--marquee-speed', (Math.sqrt(copies) * factor) + 's');

      if (isResize) {
        var wrapper = this._wrapper;
        requestAnimationFrame(function () {
          wrapper.getAnimations().forEach(function (a) { a.currentTime = 0; });
        });
      }
    }

    _decelerate() {
      if (this._anim) return;
      var a = this._wrapper.getAnimations()[0];
      if (!a) return;
      var self = this;
      this._anim = animateValue({
        from: 1, to: 0, duration: ANIM_DURATION,
        onUpdate: function (v) { a.updatePlaybackRate(v); },
        onComplete: function () { self._anim = null; }
      });
    }

    _accelerate() {
      this._slowDown.cancel();
      var a = this._wrapper.getAnimations()[0];
      if (!a || a.playbackRate === 1) return;
      var from = this._anim ? this._anim.current : 0;
      if (this._anim) this._anim.cancel();
      var self = this;
      this._anim = animateValue({
        from: from, to: 1, duration: ANIM_DURATION,
        onUpdate: function (v) { a.updatePlaybackRate(v); },
        onComplete: function () { self._anim = null; }
      });
    }
  }

  customElements.define('cn-marquee', CnMarquee);
})();
